package org.mkui.canvas

import com.macrofocus.common.crossplatform.CPHelper
import com.macrofocus.common.properties.Property
import com.macrofocus.common.timer.CPTimer
import com.macrofocus.common.timer.CPTimerListener
import com.macrofocus.common.timing.Timing
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import kotlinx.browser.window
import org.mkui.component.CPComponent
import org.mkui.component.CPInteractiveHandler
import org.mkui.component.InteractiveComponentListeners
import react.*
import react.dom.html.ReactHTML.canvas
import web.animations.requestAnimationFrame
import web.canvas.CanvasRenderingContext2D
import web.canvas.ID
import web.cssom.*
import web.html.HTMLCanvasElement

const val HIDPI = true

@JsExport
external interface FunctionalReactCanvasProps : Props {
}

class FunctionalCPCanvas(
    val layers: MutableList<CanvasRenderingContext2DLayer>,
    private val listeners: List<CanvasListener>,
    private val showTiming: Property<Boolean>
) : CPComponent {
    private val dirtyLayers: MutableSet<CanvasRenderingContext2DLayer> = HashSet()
    protected val timer: CPTimer = CPHelper.instance.createTimer("Canvas\$Prepare", 40, true, object : CPTimerListener {
        override fun timerTriggered() {
            prepare()
        }
    })

    val prepareTiming = Timing()
    val renderTiming = Timing()
    val interactTiming = Timing()

    fun schedulePrepare(layer: CanvasRenderingContext2DLayer) {
        dirtyLayers.add(layer)
        if (isShowing) {
            timer.restart()
        }
    }

    fun schedulePrepare() {
        scheduleRender()
        for (layer in layers) {
            dirtyLayers.add(layer)
        }
        if (isShowing) {
            timer.restart()
        }
    }

    private fun prepare() {
        prepareTiming.start()
        val width = width
        val height = height
        var dirtyLayers: List<CanvasRenderingContext2DLayer>
        run<Unit> {
            dirtyLayers = ArrayList<CanvasRenderingContext2DLayer>(this.dirtyLayers)
            this.dirtyLayers.clear()
        }
        for (layer in dirtyLayers) {
            layer.prepare(width, height)
        }
        prepareTiming.stop()
        scheduleRender()
    }

    fun scheduleRender() {
        redraw()
    }

    val isShowing = true

    private lateinit var resizeObserver: ResizeObserver

    private val interactiveListeners = InteractiveComponentListeners()

    var width = 0
    var height = 0
    var scaleFactor = 1.0
    var physicalWidth = 0
    var physicalHeight = 0

    fun redraw() {
        requestAnimationFrame { paintTheCanvas() }
    }

    private fun paintTheCanvas() {
//        val start = Date.now()
        renderTiming.start()

        ctx?.apply {
//            val width = canvas.clientWidth
//            val height = canvas.clientHeight
//            val scaleFactor = findDesiredScaleFactor()
//            val dim = canvas.getBoundingClientRect()
//            if(dimensionHasChanged(width, height, scaleFactor)) {
//                syncCanvasDimension(width, height, scaleFactor)
//            }

            clearAndResetRenderingContext2D(this)

//            for (i in layers) {
//                i.prepare(width, height)
//            }

            for (i in layers) {
                i.render(this)
            }

            renderTiming.stop()

            if (showTiming.value) {
                fillStyle = "red"
                var y = height.toDouble()
                y -= 12.0
                fillText("Prepare: " + prepareTiming.lastDuration + " ms", 5.0, y)
                y -= 12
                fillText("Render: " + renderTiming.lastDuration + " ms", 5.0, y)
                y -= 12
                fillText("Interact: " + interactTiming.lastDuration + " ms", 5.0, y)

//                val delta = (Date.now() - start)
//                val fps = 1 / delta

//                fillStyle = "red"
//                fillText("" + delta.absoluteValue + " ms", 5.0, height - 12.0)
            }
        }

//        window.requestAnimationFrame { paintTheCanvas() }
    }

    private fun findDesiredScaleFactor(): Double {
        return if (HIDPI)
            window.devicePixelRatio
        else
            1.0
    }

    fun dimensionHasChanged(width: Int, height: Int, scaleFactor: Double): Boolean {
        return width > 0 && height > 0 && this.width != width || this.height != height || this.scaleFactor != scaleFactor
    }

    fun syncCanvasDimension(canvasRef: RefObject<HTMLCanvasElement>, width: Int, height: Int, scaleFactor: Double) {
        println("syncCanvasDimension  ${this.width} x ${this.height} - $width x $height")
        this.width = width
        this.height = height
        this.scaleFactor = scaleFactor
        this.physicalWidth = (width * scaleFactor).toInt()
        this.physicalHeight = (height * scaleFactor).toInt()
        val canvas = canvasRef.current
        if (canvas != null) {
            println("Resizing " + width + "x" + height + "@" + scaleFactor + " -> " + physicalWidth + "x" + physicalHeight)
            canvas.width = physicalWidth
            canvas.height = physicalHeight
        }
    }

    fun clearAndResetRenderingContext2D(g: CanvasRenderingContext2D) {
        // This seems like a hack, but works better because it reset the graphic context
//            canvas.setCoordinateSpaceWidth(canvas.getOffsetWidth());
        g.setTransform(scaleFactor, 0.0, 0.0, scaleFactor, 0.0, 0.0)
        g.clearRect(0.0, 0.0, width.toDouble(), height.toDouble())
    }

    private fun publishData(canvasRef: RefObject<HTMLCanvasElement>, canvas: HTMLCanvasElement) {
        val width = canvas.clientWidth // Todo: offsetWidth
        val height = canvas.clientHeight // Todo: offsetHeight
//        val width = canvas.offsetWidth // Todo: offsetWidth
//        val height = canvas.offsetHeight // Todo: offsetHeight
        println("client=${canvas.clientWidth}x${canvas.clientHeight}, offset=${canvas.offsetWidth}x${canvas.offsetHeight}, offset=${canvas.scrollWidth}x${canvas.scrollHeight}")
        println("publishData from ${canvasRef} " + width + "x" + height)
        val scaleFactor = findDesiredScaleFactor()
        val dim = canvas.getBoundingClientRect()
        if (dimensionHasChanged(width, height, scaleFactor)) {
            syncCanvasDimension(canvasRef, width, height, scaleFactor)
            notifySizeChanged(width, height)
            schedulePrepare()
        }
    }

    override val nativeComponent = FC<FunctionalReactCanvasProps> {
        val canvasRef = useRef<HTMLCanvasElement>()

        useEffect {
            val targetContainer = canvasRef.current!!
            println("Mount " + targetContainer)

            ctx = canvasRef.current?.getContext(CanvasRenderingContext2D.ID)

            resizeObserver = ResizeObserver { _, _ -> publishData(canvasRef, targetContainer) }
            resizeObserver.observe(targetContainer)

            CPInteractiveHandler(targetContainer, interactiveListeners, interactTiming)

            awaitCancellation {
                println("Unmount " + targetContainer)
                resizeObserver.disconnect()
            }
        }

        canvas {
            className = ClassName("canvas")
            style = unsafeJso {
                width = 100.pct
                height = 100.pct
                position = Position.absolute
                left = 0.px
                right = 0.px
                top = 0.px
                bottom = 0.px

                maxWidth = 100.pct
                maxHeight = 100.pct
                objectFit = ObjectFit.contain

                pointerEvents = Auto.auto
            }

            ref = canvasRef
        }
//        window.requestAnimationFrame { paintTheCanvas() }
    }

    var ctx: CanvasRenderingContext2D? = null
        private set

    protected fun notifySizeChanged(width: Int, height: Int) {
        for (listener in listeners) {
            listener.sizeChange(width, height)
        }
    }

    fun addMouseListener(l: MouseListener) {
        interactiveListeners.addMouseListener(l)
    }

    fun addMouseMotionListener(l: MouseMotionListener) {
        interactiveListeners.addMouseMotionListener(l)
    }

    fun removeMouseListener(l: MouseListener) {
        interactiveListeners.removeMouseListener(l)
    }

    fun removeMouseMotionListener(l: MouseMotionListener) {
        interactiveListeners.removeMouseMotionListener(l)
    }

    fun addMouseWheelListener(l: MouseWheelListener) {
        interactiveListeners.mouseWheelListeners.add(l)
    }

    fun addKeyListener(l: KeyListener) {
        interactiveListeners.keyListeners.add(l)
    }

    fun addContextMenuListener(l: ContextMenuListener) {
        interactiveListeners.contextMenuListeners.add(l)
    }

    fun removeMouseWheelListener(l: MouseWheelListener) {
        interactiveListeners.mouseWheelListeners.remove(l)
    }

    fun removeKeyListener(l: KeyListener) {
        interactiveListeners.keyListeners.remove(l)
    }

    fun removeContextMenuListener(l: ContextMenuListener) {
        interactiveListeners.contextMenuListeners.remove(l)
    }
}